/**
 * useFeeManagementData
 * FeeManagement 상태, API 호출, 페이지네이션을 담당하는 훅
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { usePagination } from '@/shared/hooks/usePagination';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { supplierCommissionApi, SaveSupplierCommissionResponse } from '../api/supplierCommissionApi';
import { supplierApi } from '@/features/partner/supplierStatus/api/supplierApi';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import {
    FILTER_STORAGE_KEY,
    filterCommissionData,
    getUniqueSupplierNames,
    ITEMS_PER_PAGE,
    sortByStatus,
    transformCommissionData
} from '../utils/feeManagementHelpers';
import { CommissionHistoryItem, SupplierCommissionRow } from '../types/feeManagement.types';
import { useFeeManagementHandlers } from './useFeeManagementHandlers';

const DEFAULT_FILTER = {
    ALL: '전체',
} as const;

export const useFeeManagementData = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();
    const hasAppliedInitialFilter = useRef(false);

    // ======== State ========
    const [loading, setLoading] = useState<boolean>(true);
    const [commissionData, setCommissionData] = useState<SupplierCommissionRow[]>([]);
    const [allCommissionData, setAllCommissionData] = useState<SupplierCommissionRow[]>([]);

    const [defaultDailyAmount, setDefaultDailyAmount] = useState<string>('0');
    const [defaultWeeklyAmount, setDefaultWeeklyAmount] = useState<string>('0');

    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
    const [selectedSupplier, setSelectedSupplier] = useState<string>('');
    const [historyData, setHistoryData] = useState<CommissionHistoryItem[]>([]);

    const [showFeeApplyModal, setShowFeeApplyModal] = useState<boolean>(false);
    const [feeApplyDateInfo, setFeeApplyDateInfo] = useState<{
        applyStartDate: string;
        isImmediateApply: boolean;
    } | null>(null);

    const savedFilters = loadFilters<{ partnerId: string; statusFilter: string }>(FILTER_STORAGE_KEY);
    const [partnerId, setPartnerId] = useState<string>(savedFilters?.partnerId || DEFAULT_FILTER.ALL);
    const [statusFilter, setStatusFilter] = useState<string>(savedFilters?.statusFilter || '');

    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: commissionData, itemsPerPage: ITEMS_PER_PAGE });

    // ======== Derived Values ========
    const supplierNames = getUniqueSupplierNames(allCommissionData);

    // ======== API Calls ========
    const fetchDefaultFeeAmounts = async () => {
        try {
            const response = await commonCodeApi.getCommonCodeList('FEE_NAME');
            const hqPreCode = response.codes.find(code => code.comCode === 'HQPRE');
            if (hqPreCode && hqPreCode.val3) setDefaultDailyAmount(hqPreCode.val3);
            const hqPostCode = response.codes.find(code => code.comCode === 'HQPOST');
            if (hqPostCode && hqPostCode.val3) setDefaultWeeklyAmount(hqPostCode.val3);
        } catch {
            // 기본값 유지 (0원)
        }
    };

    const fetchSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            const [commissions, supplierDetails] = await Promise.all([
                supplierCommissionApi.getAllSupplierCommissions(),
                supplierApi.getSupplierDetailList()
            ]);

            const statusMap = new Map(supplierDetails.suppliers.map(s => [s.suppId, s.statusCode]));
            const commissionsWithStatus = commissions.map(comm => ({
                ...comm,
                status: statusMap.get(comm.supplierId) || 'OPERATING'
            }));

            const initialData = transformCommissionData(commissionsWithStatus);
            const sortedData = sortByStatus(initialData);
            setAllCommissionData(sortedData);
            setCommissionData(sortedData);
        } catch {
            showToast('본사 수수료를 불러오는데 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const saveCommission = useCallback(async (row: SupplierCommissionRow): Promise<SaveSupplierCommissionResponse | null> => {
        const userInfo = getUserInfo();
        if (!userInfo) {
            showToast('사용자 정보를 가져올 수 없습니다.', { type: ToastType.ERROR });
            return null;
        }

        try {
            const response = await supplierCommissionApi.saveSupplierCommission({
                suppId: row.supplierId,
                dailyCommissionType: row.dailyCommissionType || null,
                dailyAmount: row.dailyAmount ? parseInt(row.dailyAmount.replace(/,/g, '')) : null,
                weeklyCommissionType: row.weeklyCommissionType || null,
                weeklyAmount: row.weeklyAmount ? parseInt(row.weeklyAmount.replace(/,/g, '')) : null,
                weeklyPaybackUseYn: row.weeklyPaybackUseYn || 'N',
                weeklyPaybackAmount: row.weeklyPaybackAmount ? parseInt(row.weeklyPaybackAmount.replace(/,/g, '')) : null,
                startDate: row.startDate,
                endDate: row.endDate || null,
                userId: userInfo.userId
            });
            return response;
        } catch {
            showToast('수수료 저장에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        }
    }, [getUserInfo, showToast]);

    // ======== Handlers ========
    const handlers = useFeeManagementHandlers({
        commissionData,
        allCommissionData,
        partnerId,
        statusFilter,
        defaultDailyAmount,
        defaultWeeklyAmount,
        setCommissionData,
        setStatusFilter,
        setHistoryData,
        setSelectedSupplier,
        setShowHistoryModal,
        setFeeApplyDateInfo,
        setShowFeeApplyModal,
        resetPagination,
        saveCommission,
        fetchSuppliers,
    });

    // ======== Effects ========
    useEffect(() => {
        saveFilters(FILTER_STORAGE_KEY, { partnerId, statusFilter });
    }, [partnerId, statusFilter]);

    useEffect(() => {
        fetchDefaultFeeAmounts();
        fetchSuppliers();
    }, []);

    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;
        if (!loading && allCommissionData.length > 0) {
            const saved = loadFilters<{ partnerId: string; statusFilter: string }>(FILTER_STORAGE_KEY);
            if (saved) {
                const hasFilter = saved.partnerId && saved.partnerId !== DEFAULT_FILTER.ALL;
                if (hasFilter) {
                    hasAppliedInitialFilter.current = true;
                    const filtered = filterCommissionData(allCommissionData, saved.partnerId, saved.statusFilter || '');
                    setCommissionData(filtered);
                    resetPagination();
                }
            }
        }
    }, [loading, allCommissionData.length]);

    return {
        // state
        loading,
        commissionData,
        supplierNames,
        partnerId,
        setPartnerId,
        statusFilter,
        setStatusFilter,
        // modal state
        showHistoryModal,
        setShowHistoryModal,
        selectedSupplier,
        historyData,
        showFeeApplyModal,
        setShowFeeApplyModal,
        feeApplyDateInfo,
        setFeeApplyDateInfo,
        // pagination
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        // handlers
        ...handlers,
    };
};
